import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { ChecklightSessionStatus } from '@thesavingsgroup/enums/ChecklightSessionStatus';
import { UploadItemType } from '@thesavingsgroup/enums/UploadItemType';
import { VehicleInformationType } from '@thesavingsgroup/enums/VehicleInformationType';
import { UserVehicleInformationDto } from '@thesavingsgroup/user-vehicle-information/UserVehicleInformationDto';
import { isAutopayNumber } from '@thesavingsgroup/utils/isAutopayNumber';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import i18n from 'i18next';
import find from 'lodash/find';
import lodashGet from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { StateCreator } from 'zustand';

import { ProgressStep } from '../progressStore/types/ProgressStep';
import { Task } from '../taskStore/types/Task';
import { DEFAULT_VEHICLE_APPLICATION_OWNER } from './constants/defaultVehicleApplicationOwner';
import { getVehicleApplicationDescription } from './functions/getVehicleApplicationDescription';
import { getVehicleApplicationType } from './functions/getVehicleApplicationType';
import { getVehicleBrand } from './functions/getVehicleBrand';
import { getVehicleDefaultImage } from './functions/getVehicleDefaultImage';
import { getVehicleDescription } from './functions/getVehicleDescription';
import { getVehicleInformationByType } from './functions/getVehicleInformationByType';
import { getVehicleLeadChannelCode } from './functions/getVehicleLeadChannelCode';
import { getVehicleRouteRedirect } from './functions/getVehicleRouteRedirect';
import { isVehicleApplicationInSubmission } from './functions/isVehicleApplicationInSubmission';
import { isVehicleApplicationRefandiType } from './functions/isVehicleApplicationRefandiType';
import { Application } from './types/Application';
import { DashboardState } from './types/DashboardState';
import { DashboardStateDependencies } from './types/DashboardStateDependencies';
import { DashboardVehicle } from './types/DashboardVehicle';

export const dashboardStore =
  ({
    useApiStore,
    useLeadChannelStore,
    useProgressStore,
    useTaskStore,
    useUserStore,
  }: DashboardStateDependencies): StateCreator<DashboardState> =>
  (set, get) => ({
    currentVehicle: null,
    fetchVehicleByApplicationAutopayNumberFailedMessage: null,
    fetchVehicleByIdFailedMessage: null,
    fetchVehiclesFailedMessage: null,
    fetchVehiclesSuccess: false,
    isFetchingVehicleByApplicationAutopayNumber: false,
    isFetchingVehicleById: false,
    isFetchingVehicles: false,
    vehicles: [],

    computed: {
      get isVehicleApplicationEmployeeMissingDetails(): boolean {
        const { currentVehicle } = get();

        const applicationOwnerFirstName: string =
          currentVehicle?.application?.applicationOwner?.firstName;
        const applicationOwnerLastName: string =
          currentVehicle?.application?.applicationOwner?.lastName;

        const applicationOwnerPhoneNumber =
          currentVehicle?.application?.applicationOwner?.phoneNumber;

        const applicationOwnerEmail =
          currentVehicle?.application?.applicationOwner?.emailAddress;

        return [
          applicationOwnerFirstName,
          applicationOwnerLastName,
          applicationOwnerPhoneNumber,
          applicationOwnerEmail,
        ].some(isEmpty);
      },
      get isVehicleConfirmed(): boolean {
        const { currentVehicle } = get();
        const isConfirmed = currentVehicle?.isConfirmed;

        return isConfirmed;
      },
      get isVehicleEmpty(): boolean {
        const { currentVehicle } = get();

        return isEmpty(currentVehicle);
      },
      get vehicleApplicationAutopayNumber(): string | undefined {
        const { currentVehicle } = get();

        return currentVehicle?.application?.autopayNumber;
      },
      get vehicleApplicationAutopayNumberTrimmed(): string {
        const {
          computed: { vehicleApplicationAutopayNumber },
        } = get();

        if (!vehicleApplicationAutopayNumber) {
          return '';
        }

        const identifierStart = 6;

        return vehicleApplicationAutopayNumber.substring(identifierStart);
      },
      get vehicleApplicationDescription(): string {
        const { currentVehicle } = get();

        return getVehicleApplicationDescription(currentVehicle);
      },
      get vehicleApplicationEmployeeEmail(): string {
        const {
          computed: {
            isVehicleApplicationEmployeeMissingDetails,
            vehicleBrand,
          },
          currentVehicle,
        } = get();

        if (isVehicleApplicationEmployeeMissingDetails) {
          return DEFAULT_VEHICLE_APPLICATION_OWNER[vehicleBrand].emailAddress;
        }

        return currentVehicle?.application?.applicationOwner?.emailAddress;
      },
      get vehicleApplicationEmployeeName(): string {
        const {
          computed: {
            isVehicleApplicationEmployeeMissingDetails,
            vehicleBrand,
          },
          currentVehicle,
        } = get();

        if (isVehicleApplicationEmployeeMissingDetails) {
          return DEFAULT_VEHICLE_APPLICATION_OWNER[vehicleBrand].name;
        }

        const applicationOwnerFirstName: string =
          currentVehicle?.application?.applicationOwner?.firstName ?? '';
        const applicationOwnerLastName: string =
          currentVehicle?.application?.applicationOwner?.lastName ?? '';

        return `${applicationOwnerFirstName} ${applicationOwnerLastName.charAt(
          0,
        )}.`;
      },
      get vehicleApplicationEmployeePhone(): string {
        const {
          computed: {
            isVehicleApplicationEmployeeMissingDetails,
            vehicleBrand,
          },
          currentVehicle,
        } = get();

        if (isVehicleApplicationEmployeeMissingDetails) {
          return toPhoneString(
            DEFAULT_VEHICLE_APPLICATION_OWNER[vehicleBrand].phoneNumber,
          );
        }

        const applicationOwnerPhoneNumber =
          currentVehicle?.application?.applicationOwner?.phoneNumber;

        return toPhoneString(applicationOwnerPhoneNumber);
      },
      get vehicleApplicationHasCoapplicant(): boolean {
        const { currentVehicle } = get();

        return currentVehicle?.application?.hasCoApplicant;
      },
      get vehicleApplicationId(): string | undefined {
        const { currentVehicle } = get();

        return currentVehicle?.application?.id;
      },
      get vehicleApplicationInSubmission(): boolean {
        const { currentVehicle } = get();

        return isVehicleApplicationInSubmission(currentVehicle);
      },
      get vehicleApplicationIsFailedSubmission(): boolean {
        const {
          computed: { vehicleApplicationStatus },
        } = get();

        return (
          vehicleApplicationStatus === ChecklightSessionStatus.FAILED_SUBMISSION
        );
      },
      get vehicleApplicationIsLeaseBuyoutType(): boolean {
        const { currentVehicle } = get();

        if (!currentVehicle) {
          return false;
        }

        return (
          currentVehicle.application?.applicationType ===
          ApplicationType.LEASE_BUYOUT
        );
      },
      get vehicleApplicationIsQueuedForSubmission(): boolean {
        const {
          computed: { vehicleApplicationStatus },
        } = get();

        return (
          vehicleApplicationStatus ===
          ChecklightSessionStatus.QUEUED_FOR_SUBMISSION
        );
      },
      get vehicleApplicationIsRefandiType(): boolean {
        const { currentVehicle } = get();

        return isVehicleApplicationRefandiType(currentVehicle);
      },
      get vehicleApplicationIsRefinanceType(): boolean {
        const { currentVehicle } = get();

        if (!currentVehicle) {
          return false;
        }

        return (
          currentVehicle.application?.applicationType ===
          ApplicationType.REFINANCE
        );
      },
      get vehicleApplicationLeadChannelCode(): string | undefined {
        const { currentVehicle } = get();

        return getVehicleLeadChannelCode(currentVehicle);
      },
      get vehicleApplicationProgress(): ProgressStep[] {
        const {
          computed: {
            vehicleApplicationSourceStatus,
            vehicleApplicationStatus,
          },
        } = get();

        // Prefer source status if available
        if (vehicleApplicationSourceStatus) {
          return useProgressStore
            .getState()
            .getStepsByApplicationStatus(vehicleApplicationSourceStatus);
        }

        return useProgressStore
          .getState()
          .getStepsByApplicationStatus(vehicleApplicationStatus);
      },
      get vehicleApplicationProgressPreview(): ProgressStep[] {
        const {
          computed: {
            vehicleApplicationSourceStatus,
            vehicleApplicationStatus,
          },
        } = get();

        // Prefer source status if available
        if (vehicleApplicationSourceStatus) {
          return useProgressStore
            .getState()
            .getPreviewStepsByApplicationStatus(vehicleApplicationSourceStatus);
        }

        return useProgressStore
          .getState()
          .getPreviewStepsByApplicationStatus(vehicleApplicationStatus);
      },
      get vehicleApplicationSourceStatus(): string | undefined {
        const { currentVehicle } = get();

        return currentVehicle?.application?.sourceStatus;
      },
      get vehicleApplicationStatus(): ChecklightSessionStatus | undefined {
        const { currentVehicle } = get();

        return currentVehicle?.application?.status;
      },
      get vehicleApplicationTask(): Task {
        const {
          computed: {
            vehicleApplicationSourceStatus,
            vehicleApplicationStatus,
          },
        } = get();
        const { getTaskByApplicationStatus } = useTaskStore.getState();

        // Prefer source status if available
        if (vehicleApplicationSourceStatus) {
          return getTaskByApplicationStatus(vehicleApplicationSourceStatus);
        }

        return getTaskByApplicationStatus(vehicleApplicationStatus);
      },
      get vehicleApplicationType(): string | undefined {
        const { currentVehicle } = get();

        return getVehicleApplicationType(currentVehicle);
      },
      get vehicleApplicationUniqueIdentifier(): string | undefined {
        const { currentVehicle } = get();

        return currentVehicle?.application?.uniqueIdentifier;
      },
      get vehicleApplicationUploadedTypes(): UploadItemType[] {
        const { currentVehicle } = get();

        return currentVehicle?.application?.verifications ?? [];
      },
      get vehicleBrand(): string {
        const { currentVehicle } = get();

        return getVehicleBrand(currentVehicle);
      },
      get vehicleDefaultImage(): string | null | undefined {
        const { currentVehicle } = get();

        if (!currentVehicle) {
          return null;
        }

        return getVehicleDefaultImage(currentVehicle);
      },
      get vehicleDescription(): string {
        const { currentVehicle } = get();

        return getVehicleDescription(currentVehicle);
      },
      get vehicleEmbeddedInsuranceInformation():
        | UserVehicleInformationDto
        | undefined {
        const { currentVehicle } = get();

        const information = lodashGet(currentVehicle, 'information');

        return getVehicleInformationByType(
          information,
          VehicleInformationType.EMBEDDED_INSURANCE,
        );
      },
      get vehicleEmbeddedInsuranceLeadId(): string | undefined {
        const {
          computed: { vehicleEmbeddedInsuranceInformation },
        } = get();

        return toString(
          lodashGet(vehicleEmbeddedInsuranceInformation, 'details.leadId'),
        );
      },
      get vehicleId(): string | undefined {
        const { currentVehicle } = get();

        return currentVehicle?.id;
      },
      get vehicleYearMakeModel(): string | null {
        const { currentVehicle } = get();

        if (
          !currentVehicle ||
          !currentVehicle.year ||
          !currentVehicle.make ||
          !currentVehicle.model
        ) {
          return null;
        }

        return `${currentVehicle.year} ${currentVehicle.make} ${currentVehicle.model}`;
      },
    },

    addVehicle(vehicle: DashboardVehicle | null): void {
      if (!vehicle) {
        return;
      }

      const { vehicles } = get();

      set({ vehicles: [vehicle, ...vehicles] });
    },
    fetchVehicleApplicationById(
      vehicleId: string,
      applicationId: string,
    ): Promise<Application | null> {
      if (!vehicleId || !applicationId) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<Application>(
          `/2.0/vehicles/${vehicleId}/application/${applicationId}`,
        )
        .then((application): Application => application)
        .catch((): any => null);
    },
    async fetchVehicleByApplicationAutopayNumber(
      autopayNumber: string,
    ): Promise<void> {
      if (!autopayNumber) {
        return;
      }

      set({
        currentVehicle: null,
        fetchVehicleByApplicationAutopayNumberFailedMessage: null,
        isFetchingVehicleByApplicationAutopayNumber: true,
      });

      const { fetchVehicles, setCurrentVehicle } = get();

      if (isEmpty(get().vehicles)) {
        await fetchVehicles();
      }

      const foundVehicle = find(
        get().vehicles,
        (vehicle): boolean =>
          vehicle.application?.autopayNumber === autopayNumber,
      );

      if (foundVehicle) {
        setCurrentVehicle(foundVehicle);
      } else {
        set({
          fetchVehicleByApplicationAutopayNumberFailedMessage: i18n.t(
            'errors.fetchVehicleByApplicationAutopayNumber.defaultMessage',
          ),
        });
      }

      set({ isFetchingVehicleByApplicationAutopayNumber: false });
    },
    fetchVehicleById(vehicleId: string | undefined): Promise<null> {
      if (!vehicleId) {
        return;
      }

      set({
        currentVehicle: null,
        fetchVehicleByIdFailedMessage: null,
        isFetchingVehicleById: true,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<DashboardVehicle>(`/2.0/vehicles/${vehicleId}`)
        .then((vehicle): any => {
          get().setCurrentVehicle(vehicle);

          return null;
        })
        .catch((error: unknown): any => {
          const errorStatus = toString(
            lodashGet(error as any, 'response.status'),
          );

          const defaultMessage = i18n.t(
            'errors.fetchVehicleById.defaultMessage',
          );

          set({
            fetchVehicleByIdFailedMessage: lodashGet(
              i18n.t('errors.fetchVehicleById.statusMessage'),
              errorStatus,
              defaultMessage,
            ),
          });

          return null;
        })
        .finally((): void => {
          set({ isFetchingVehicleById: false });
        });
    },
    fetchVehicles(): Promise<void> {
      const portalHttpRequest = useApiStore.getState().portalApi();

      set({
        fetchVehiclesFailedMessage: null,
        fetchVehiclesSuccess: false,
        isFetchingVehicles: true,
      });

      return portalHttpRequest
        .get<DashboardVehicle[]>('/2.0/vehicles')
        .then((vehicles): void => {
          set({ fetchVehiclesSuccess: true, vehicles });
        })
        .catch((error: unknown): void => {
          const errorStatus = toString(
            lodashGet(error as any, 'response.status'),
          );

          const defaultMessage = i18n.t('errors.fetchVehicles.defaultMessage');

          set({
            fetchVehiclesFailedMessage: lodashGet(
              i18n.t('errors.fetchVehicles.statusMessage'),
              errorStatus,
              defaultMessage,
            ),
          });
        })
        .finally((): void => {
          set({ isFetchingVehicles: false });
        });
    },
    async loadCurrentVehicle(vehicleId: string | undefined): Promise<void> {
      if (!vehicleId) {
        return;
      }

      if (isAutopayNumber(vehicleId)) {
        await get().fetchVehicleByApplicationAutopayNumber(vehicleId);

        return;
      }

      await get().fetchVehicleById(vehicleId);
    },
    async loader(
      vehicleId: string | undefined,
      request: Request,
      hasDuplicateApplication?: string | null,
    ): Promise<Response | null> {
      const authRedirect = await useUserStore
        .getState()
        .ensureSessionForRouteLoader();

      if (authRedirect) {
        return authRedirect;
      }

      if (!vehicleId) {
        return getVehicleRouteRedirect(
          request.url,
          null,
          vehicleId,
          hasDuplicateApplication,
        );
      }

      // known from the garage list: settle branding only, page fetches the vehicle itself
      const known = find(
        get().vehicles,
        (vehicle): boolean => vehicle.id === vehicleId,
      );

      if (known) {
        await useLeadChannelStore
          .getState()
          .loadLeadChannel(getVehicleLeadChannelCode(known));
      } else {
        // cold entry: fetch the vehicle so its brand can be settled before render
        await get().loadCurrentVehicle(vehicleId);
        await useLeadChannelStore.getState().ensureLeadChannelLoaded();
      }

      // the garage entry when the branch above skipped fetching, otherwise whatever
      // it just fetched, which is also how an autopay number resolves - the garage
      // list is keyed by id
      return getVehicleRouteRedirect(
        request.url,
        known || get().currentVehicle,
        vehicleId,
        hasDuplicateApplication,
      );
    },
    setCurrentVehicle(vehicle: DashboardVehicle | null): void {
      if (!vehicle) {
        return;
      }

      set({ currentVehicle: vehicle });
    },
  });
